import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Building2, Users, Settings, HelpCircle, LogOut, X } from 'lucide-react';
import Icon from '../components/dashboard/DashboardIcons';

const STUDIO_ITEMS = [
  { label: 'لوحة العمل الفني', icon: 'dumbbell', path: '/coach/studio', testId: 'more-sheet-studio' },
  { label: 'مكتبة التمارين', icon: 'field', path: '/coach/studio/exercises', testId: 'more-sheet-exercises' },
  { label: 'إنشاء تمرين', icon: 'plus', path: '/coach/studio/exercises/new', testId: 'more-sheet-new-exercise' },
  { label: 'الخطط التدريبية', icon: 'clipboard', path: '/coach/studio/plans', testId: 'more-sheet-plans' },
  { label: 'جدول التدريبات', icon: 'calendar', path: '/coach/studio/schedule', testId: 'more-sheet-schedule' },
  { label: 'التقارير الفنية', icon: 'chart', path: '/coach/studio/reports', testId: 'more-sheet-reports' },
];

export default function CoachMobileMoreSheet({ onClose, onLogout, onOpenSupport }) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const navigate = useNavigate();
  const location = useLocation();
  const go = (path) => { onClose(); navigate(path); };
  const isStudioActive = (path) => location.pathname === path || (path !== '/coach/studio' && location.pathname.startsWith(path + '/'));

  const ACCOUNT_ITEMS = [
    { label: 'المنشآت المرتبطة', icon: Building2, action: () => go('/coach/organizations'), testId: 'more-sheet-orgs' },
    { label: 'طاقم العمل', icon: Users, action: () => go('/coach/crew'), testId: 'more-sheet-crew' },
    { label: 'إعدادات الحساب والخصوصية', icon: Settings, action: () => go('/coach/profile'), testId: 'more-sheet-settings' },
    { label: 'مركز الدعم والمساعدة', icon: HelpCircle, action: () => { onClose(); onOpenSupport(); }, testId: 'more-sheet-support' },
  ];

  return (
    <div className="fixed inset-0 z-[60] flex flex-col justify-end bg-slate-900/40 backdrop-blur-[2px] lg:hidden" data-testid="more-sheet-container" onClick={onClose}>
      <div
        className="relative max-h-[85vh] animate-sheet-up space-y-5 overflow-y-auto rounded-t-[28px] bg-white p-5 shadow-[0_-12px_40px_rgba(15,23,42,0.18)]"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 16px) + 20px)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto -mt-1 mb-1 h-1.5 w-12 rounded-full bg-slate-200" />
        <div className="flex items-center justify-between px-1 pb-1">
          <h4 className="text-base font-bold text-slate-900">المزيد</h4>
          <button onClick={onClose} data-testid="more-sheet-close-btn" className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div data-testid="more-sheet-studio-group">
          <div className="mb-2 px-1 text-[11px] font-bold uppercase tracking-wide text-teal-600">العمل الفني</div>
          <div className="grid grid-cols-3 gap-2.5">
            {STUDIO_ITEMS.map((item) => (
              <button
                key={item.testId}
                onClick={() => go(item.path)}
                data-testid={item.testId}
                className={`flex flex-col items-center gap-1.5 rounded-2xl border px-2 py-3.5 text-center transition-all active:scale-95 ${
                  isStudioActive(item.path) ? 'border-teal-300 bg-teal-50' : 'border-slate-100 bg-slate-50/70'
                }`}
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 text-white shadow-sm">
                  <Icon name={item.icon} className="h-4.5 w-4.5" strokeWidth={2} />
                </span>
                <span className="text-[11px] font-bold leading-tight text-slate-700">{item.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div data-testid="more-sheet-account-group">
          <div className="mb-2 px-1 text-[11px] font-bold uppercase tracking-wide text-slate-400">حسابي والمنشآت</div>
          <div className="space-y-2">
            {ACCOUNT_ITEMS.map((item) => (
              <button
                key={item.testId}
                onClick={item.action}
                data-testid={item.testId}
                className="flex w-full items-center gap-3 rounded-2xl border border-slate-100 bg-white px-4 py-3.5 text-sm font-semibold text-slate-700 transition-colors active:scale-[0.98] hover:border-teal-200 hover:bg-teal-50/60"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-teal-50">
                  <item.icon className="h-4.5 w-4.5 text-teal-600" />
                </span>
                {item.label}
              </button>
            ))}
          </div>
        </div>

        <button
          onClick={() => { onClose(); onLogout(); }}
          data-testid="more-sheet-logout"
          className="flex w-full items-center gap-3 rounded-2xl border border-red-100 bg-red-50/60 px-4 py-3.5 text-sm font-semibold text-red-600 transition-colors active:scale-[0.98] hover:bg-red-50"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-red-100/70">
            <LogOut className="h-4.5 w-4.5" />
          </span>
          تسجيل الخروج
        </button>
      </div>
      <style>{`
        @keyframes sheet-up { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
        .animate-sheet-up { animation: sheet-up 0.22s ease-out; }
      `}</style>
    </div>
  );
}
